import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Box, Grid, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { lighten, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { LoginLogoImg, Page } from '@roc/feature-app-core';
import { Button, Paper, PaperBorderType, PreviewBackdrop } from '@roc/ui';
import clsx from 'clsx';
import jsPDF from 'jspdf';
import { useEffect, useRef, useState } from 'react';
import { downloadDocument, formatDate, getPdfBlobUrl } from '@roc/feature-utils';
import axios from 'axios';
import { GetApp } from '@material-ui/icons';
import { blue } from '@material-ui/core/colors';
import { mockSignature } from './mock/mockSignature';
import { RequestCreditAuthStore } from 'libs/client-portal-shared/src/app/modules/creditBackground/stores/requestCreditAuthStore';
import { PublicCreditAuthorizationStore } from 'libs/client-portal-shared/src/app/modules/creditBackground/stores/publicCreditAuthorizationStore';

const useStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    paddingLeft: '32px',
    paddingRight: '32px',
    position: 'relative',
    background: '#FFF',
    fontSize: 16,
    overFlowX: 'auto',
    '& table': {
      minHeight: '40px',
      width: '100%',
      textAlign: 'left',
      borderCollapse: "collapse",
      border: '1px solid #eee',
    },
    '& table th': {
      textAlign: 'left',
      fontWeight: 'bold',
      background: '#f4f4f4',
      padding: '6px 24px 6px 16px',
      borderBottom: '1px solid #eee',
    },
    '& table tr:last-child th': {
      borderBottom: 'none',
    },
    '& table td': {
      color: 'blue',
      borderBottom: '1px solid #eee',
    },
    '& table tr:last-child td': {
      borderBottom: 'none',
    },
    '@media (max-width: 800px)': {
      fontSize: 8,
      padding: 0,
    },
  },
  documentCard: {
    minWidth: '650px',
    borderRadius: 0,
    borderColor: '#aaa',
  },
  requirementsStepCard: {
    minWidth: 0,
    '& div > p': {
      marginTop: '20px',
      background: lighten(theme.palette.primary.main, 0.8),
    },
  },
  requirementsFooterCard: {
    minWidth: 0,
  },
  documentCardTitle: {
    justifyContent: 'center',
    textAlign: 'center',
    background: lighten(theme.palette.primary.main, 0.5),
    fontWeight: 'bold',
    padding: theme.spacing(0.5),
  },
  logoImgContainer: {
    alignItems: 'center',
    margin: '0',
    padding: '16px 16px 0 16px',
  },
  pdfBodyContent: {
    margin: '16px 0',
    fontSize: 12,
    textAlign: 'justify',
  }
}));

const usePrintModeStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    fontSize: 12,
    padding: '0 32px',
    '& table th': {
      fontSize: 12
    },
    '& table td': {
      fontSize: 12
    },
    '& ul,ol': {
      marginLeft: theme.spacing(2),
    },
    '& h6': {
      fontSize: 12,
    },
  },
}));

const usePrintStyles = makeStyles(() => ({
  print: {
    width: '210mm',
    minHeight: '297mm',
    height: 'auto',
    ['& [data-testid="documentMenuButton"]']: {
      display: 'none !important',
    },
  },
}));

export const CAF_PDF_HTML = ({
  preview = false,
  printMode = false,
  setContainerRef,
  publicCreditAuthorizationStore,
  signature,
}: {
  preview?: boolean,
  printMode?: boolean,
  setContainerRef: any,
  publicCreditAuthorizationStore: PublicCreditAuthorizationStore
  signature
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const containerRef = useRef(null);

  const [printModeContainerStyle, setPrintModeContainerStyle] = useState<any>();
  const [containerStyle, setContainerStyle] = useState<any>();
  const printModeClasses = usePrintModeStyles();


  const [openDownloanPrintModal, setOpenDownloanPrintModal] = useState(false);
  const printClasses = usePrintStyles();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);

  const values = publicCreditAuthorizationStore.personalInformationFormStore.getFormValues()

  const onDownloadPrintPdf = async () => {
    setPdfLoading(true);
    const x = await getPdfBlobUrl(containerRef.current, {
      scale: 4,
      onclone: (doc, el) => el.className += ' ' + printClasses.print,
      useCORS: true
    }).then((url) => {
      return axios.get(url.toString(), { responseType: 'arraybuffer' })
        .then(response => {
          setPdfData(response.data);
          setPdfLoading(false);
          return response.data;
        })
        .catch(error => {
          console.error('Error fetching PDF data:', error);
        });
    });
    return x;
  };

  useEffect(() => {
    const doc = new jsPDF("p", "mm", "a4");
    let width: any = doc.internal.pageSize.getWidth();
    let height: any = doc.internal.pageSize.getHeight();
    width = `${Math.floor(width)}mm`;
    height = `${Math.floor(height)}mm`;
    setPrintModeContainerStyle({
      margin: 'auto',
      minWidth: width,
      maxWidth: width,
      height: height
    });
    setContainerStyle({
      margin: 'auto',
      maxWidth: theme.breakpoints.values.lg,
    });
  }, []);

  return (
    <Box id="projectInformation">
      <div ref={containerRef} style={printMode ? printModeContainerStyle : containerStyle}>
        <Box className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          {preview && <PreviewBackdrop />}
          {/* <Box my={2} textAlign='center'>
              <LoginLogoImg containerClassName={classes.logoImgContainer} imgStyle={{ maxWidth: 260 }} />
            </Box> */}
          <Box pt={2} pb={2} textAlign='center'>

            <Typography style={{ margin: '16px 0' }} variant={'h5'}>
              Authorization to obtain Consumer and Credit Reports and release
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              I authorize and permit CCredit Checker (“Lender”), in its own name or using any trade, assumed or fictitious (“doing
              business as” or “DBA”) name, and Lender's designees, agents, brokers, participating lenders, vendors, affiliates and
              representatives (“Lender Party” or “Lender Parties”), to (a) verify my past and present credit and background history;
              and (b) order a (“hard” or “soft”) consumer credit report, business credit report (if applicable) and/or background
              check, including an investigative consumer report (any such report, a “Consumer Report”), on me.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              I also authorize and permit Lender and each Lender Party to share Credit Reports on me (or any information
              extracted from a Credit Report) to Lender's or any Lender Party's financing sources and providers, loan buyers,
              brokers, and participating lenders and representatives, and each such party's successors, assigns, potential
              successors and assigns, affiliates, designees, agents, representatives and vendors.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              I also authorize and permit Lender and each Lender Party to obtain updated credit and background verifications and
              information, and order updated Credit Reports, on a periodic basis pursuant to Lender's internal policies.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              I understand and acknowledge that neither Lender nor any Lender Party shall be responsible for any information,
              including any negative findings, which may be contained in a Credit Report. I release Lender, all Lender Parties,
              including its respective employees, officers and owners, from any and all liability for furnishing such information and
              conducting such an investigation.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              I understand that Credit Reports, and any information extracted from a Credit Report, will only be used to in
              connection with the loan/credit for which I am applying.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              A photocopy, scan or facsimile copy of this Authorization to Obtain Consumer and Credit Reports and Release shall
              be treated as though it were the original.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              I understand that, pursuant to the federal Fair Credit Reporting Act, if any adverse action is to be taken based upon
              any Credit Report, upon my request a copy of the report and a summary of the applicable consumer's rights will be
              provided to me.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              <strong>This authorization and release shall remain in full force and effect until such time as it is revoked or
                amended by me in writing.</strong>
            </Typography>

            <Table size="small" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Name:</strong>
                  </TableCell>
                  <TableCell align="left" style={{ color: 'blue' }}>{values.firstName + ' ' + values.lastName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Date of Birth:</strong>
                  </TableCell>
                  <TableCell align="left" style={{ color: 'blue' }}>{values.dateOfBirth}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Social Security Number:</strong>
                  </TableCell>
                  <TableCell align="left" style={{ color: 'blue' }}>{values.ssn}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Address:</strong>
                  </TableCell>
                  <TableCell align="left" style={{ color: 'blue' }}>{values.streetNumber + " " + values.streetName + " " + values.city + " " + values.state + " " + values.zipCode}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Email:</strong>
                  </TableCell>
                  <TableCell align="left" style={{ color: 'blue' }}>{values.emailAddress}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Cell Phone:</strong>
                  </TableCell>
                  <TableCell align="left" style={{ color: 'blue' }}>{values.phoneNumber}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box mt={3}>
              <Box mb={.5} textAlign={'left'}>
                <strong>Signature</strong>
              </Box>
              <Box display={"flex"} flexDirection={"column"} textAlign={"left"}>
                <Box>
                  <img src={signature} height={120} style={{
                    border: "1px solid #eee"
                  }} />
                </Box>
                <Box color={'blue'}>
                  {formatDate(new Date())}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </Box>

  );
}